import { createElement, useEffect, useMemo, useRef, useState } from 'react';
import { createAutocomplete } from '@algolia/autocomplete-core';
import { createLocalStorageRecentSearchesPlugin } from '@algolia/autocomplete-plugin-recent-searches';
import { getSearchSettings } from 'govbr.frontend.policy/actions/search_settings/search_settings';
import MostSearchedTerms from 'govbr.frontend.policy/components/MostSearchedTerms';
import { PropTypes } from 'prop-types';
import qs from 'query-string';
import { defineMessages, injectIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import VoiceSearch from '../VoiceSearch/VoiceSearch';

import {
  ItemTemplate,
  RecentSearchItemTemplate,
} from './SearchAutocompleteItem';

const messages = defineMessages({
  searchTermLabel: {
    id: 'Search term',
    defaultMessage: 'Search term',
  },
  whatAreYouLookingFor: {
    id: 'What are you looking for?',
    defaultMessage: 'What are you looking for?',
  },
  searchSubmitLabel: {
    id: 'Search',
    defaultMessage: 'Search',
  },
  servicesAndApps: {
    id: 'Services and apps',
    defaultMessage: 'SERVICES AND APPS',
  },
  didYouMean: {
    id: 'Did you mean?',
    defaultMessage: 'DID YOU MEAN?',
  },
  searchHistory: {
    id: 'Search history',
    defaultMessage: 'SEARCH HISTORY',
  },
});

export function SearchAutocomplete({ active, intl, history, pathname }) {
  const [focused, setFocused] = useState(false);
  const [autocompleteState, setAutocompleteState] = useState({});
  const [isClient, setIsClient] = useState(false);
  const [initialState, setInitialState] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const onFocusInput = (event) => {
    setFocused(true);
  };

  const onBlurInput = () => {
    setTimeout(() => setFocused(false), 300);
  };

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(getSearchSettings());
  }, [dispatch]);

  useEffect(() => {
    setInitialState(qs.parse(history.location.search).SearchableText || '');
  }, [history.location.search]);

  const searchSettings = useSelector((state) => state.search_settings.data);

  const autocomplete = useMemo(() => {
    if (!isClient || !searchSettings) {
      return null;
    }

    const buildSearchUrl = (origem, term) =>
      `/search?origem=${origem}&SearchableText=${term}`;

    const buildApiUrl = (term) => {
      const searchTerm = term ? `&q=${term}` : '';
      const filter = searchSettings.organ_id
        ? `&orgaoId=${searchSettings.organ_id}`
        : '';
      const site = searchSettings.site_url
        ? `&site=${searchSettings.site_url}`
        : '';
      return `${searchSettings.url_base}search/?sugestao=true&tipo=ServicoEstadual|App|Servico|Tema${searchTerm}${filter}${site}`;
    };

    const recentSearchesPlugin = createLocalStorageRecentSearchesPlugin({
      key: 'recentSearches',
      limit: 4,
      transformSource({ source, onRemove }) {
        return {
          ...source,
          templates: {
            ...source.templates,
            noResults() {
              return '';
            },
            header() {
              return intl.formatMessage(messages.searchHistory);
            },
            footer() {
              return '';
            },
            item({ item }) {
              return createElement(RecentSearchItemTemplate, {
                item: item,
                onRemove: onRemove,
              });
            },
          },
          onSelect: (props) => {
            const { itemUrl, item, setQuery, setIsOpen } = props;
            setQuery(item.label);
            setIsOpen(false);
            history.push(itemUrl);
          },
          getItemUrl: ({ item }) => {
            return buildSearchUrl('historico', item.label);
          },
        };
      },
    });

    const buildSources = ({ items: services, sugestoes: suggestions }) => {
      const sources = [];

      if (services && services.length) {
        sources.push({
          sourceId: 'services',
          getItems() {
            return services;
          },
          getItemInputValue({ item }) {
            return item.title;
          },
          getItemUrl({ item }) {
            return item.contentUrl;
          },
          onSelect: ({ itemUrl }) => {
            window.location.assign(itemUrl);
          },
          templates: {
            noResults() {
              return '';
            },
            header() {
              return intl.formatMessage(messages.servicesAndApps);
            },
            footer() {
              return '';
            },
            item({ item }) {
              return createElement(ItemTemplate, {
                icon: 'fa-grip',
                item: item.title,
              });
            },
          },
        });
      }

      if (suggestions && suggestions.length) {
        sources.push({
          sourceId: 'suggestions',
          getItems() {
            return suggestions;
          },
          getItemUrl({ item }) {
            return buildSearchUrl('sugestao', item.text);
          },
          getItemInputValue({ item }) {
            return item.text;
          },
          onSelect: ({ itemUrl, setIsOpen }) => {
            setIsOpen(false);
            history.push(itemUrl);
          },
          templates: {
            noResults() {
              return '';
            },
            item({ item }) {
              return createElement(ItemTemplate, {
                icon: 'fa-search',
                item: item.text,
              });
            },
            header() {
              return intl.formatMessage(messages.didYouMean);
            },
            footer() {
              return '';
            },
          },
        });
      }

      return sources;
    };

    const getSources = ({ query }) => {
      if (!query) return [];

      return fetch(buildApiUrl(query))
        .then((resp) => resp.json())
        .then(({ items, sugestoes }) => {
          return buildSources({ items, sugestoes });
        })
        .catch((err) => {
          return buildSources({ items: [], sugestoes: [] });
        });
    };

    const onSubmit = ({ state, event }) => {
      if (!state.query) return;
      const path =
        pathname?.length > 0 ? `&path=${encodeURIComponent(pathname)}` : '';
      history.push(
        `/search?origem=form&SearchableText=${encodeURIComponent(state.query)}${path}`,
      );
      event.preventDefault();
    };

    return createAutocomplete({
      onStateChange: ({ state }) => {
        setAutocompleteState(state);
      },
      getSources,
      onSubmit,
      initialState: {
        query: initialState,
      },
      shouldPanelOpen: ({ state }) => {
        const hasItems = state.collections.some((src) => src.items.length);
        return hasItems && state.query;
      },
      openOnFocus: true,
      detachedMediaQuery: 'none',
      plugins: [recentSearchesPlugin],
    });
  }, [isClient, searchSettings, history, initialState, pathname, intl]);

  if (!autocomplete) {
    return null;
  }

  return (
    <>
      <div
        className={`header-search ${active ? 'active' : ''}`}
        {...autocomplete.getRootProps({
          'aria-label': intl.formatMessage(messages.searchTermLabel),
        })}
      >
        <form
          className="br-input has-icon "
          role="search"
          {...autocomplete.getFormProps({ inputElement: inputRef.current })}
        >
          <input
            {...autocomplete.getInputProps({
              'aria-label': intl.formatMessage(messages.searchTermLabel),
            })}
            className="br-input "
            ref={inputRef}
            id="header-search-widget"
            autoCapitalize="off"
            type="text"
            placeholder={intl.formatMessage(messages.whatAreYouLookingFor)}
            onFocus={onFocusInput}
            onBlur={onBlurInput}
          />
          <VoiceSearch />
          <button
            className="br-button circle small"
            type="submit"
            aria-label={intl.formatMessage(messages.searchSubmitLabel)}
            title={intl.formatMessage(messages.searchSubmitLabel)}
          >
            <i className="fas fa-search" aria-hidden="true"></i>
          </button>
        </form>
        {focused && autocompleteState.isOpen && (
          <div className="aa-Panel" {...autocomplete.getPanelProps({})}>
            {autocompleteState.collections.map((collection, index) => {
              const { source, items } = collection;

              return (
                items.length > 0 && (
                  <div
                    className="br-list"
                    role="list"
                    key={`source-${index}`}
                    {...autocomplete.getListProps()}
                  >
                    {/* <div className="header"> */}
                    <div
                      className="br-item text-weight-medium pt-3"
                      role="listitem"
                    >
                      {source.templates.header()}
                    </div>
                    {/* </div> */}
                    {/* <span className="br-divider"></span> */}
                    {items.map((item, idxItem) => (
                      <Link
                        to={source.getItemUrl({ item })}
                        className="br-item"
                        role="listitem"
                        key={`${source.sourceId}-${idxItem}`}
                        {...autocomplete.getItemProps({
                          item,
                          source,
                        })}
                      >
                        {source.templates.item({ item })}
                      </Link>
                    ))}
                  </div>
                )
              );
            })}
          </div>
        )}

        {!autocompleteState.query && (
          <MostSearchedTerms searchInputFocused={focused} />
        )}
      </div>
    </>
  );
}

SearchAutocomplete.propTypes = {
  pathname: PropTypes.string,
  active: PropTypes.bool,
  history: PropTypes.object,
};

export default injectIntl(SearchAutocomplete);
